/**
 * Canonical front-end view of the six paid add-on modules.
 *
 * Mirrors MODULE_KEYS / MODULE_LABELS in services/backend/routers/modules.py.
 * Spec: .claude/specs/module-entitlement-foundation.md
 *
 * Core (People/HRIS — including Attendance and Timesheet) is deliberately absent.
 * Core is the absence of a gate, not a permanently-enabled module.
 */

export const MODULE_KEYS = [
  "recruitment",
  "finance",
  "payroll",
  "esign",
  "ai_expense_capture",
  "performance",
] as const;

export type ModuleKey = (typeof MODULE_KEYS)[number];

/**
 * What Core is called in the UI.
 *
 * "Core" is a packaging word — it describes the absence of an entitlement gate,
 * which is meaningful to billing and to this file's `moduleForPath`, and means
 * nothing to a user reading a tile next to Recruitment and Payroll. Every other
 * name in that row says what the thing does, so Core says People too.
 *
 * Display only. The key stays absent from MODULE_KEYS on purpose: giving Core a
 * module_key is what would let someone gate it by accident.
 */
export const CORE_LABEL = "People";

export const MODULE_LABELS: Record<ModuleKey, string> = {
  recruitment:        "Recruitment",
  finance:            "Finance",
  payroll:            "Payroll",
  esign:              "eSign",
  ai_expense_capture: "AI Expense Capture",
  performance:        "Performance",
};

/** Compact labels for dense table columns where the full name will not fit. */
export const MODULE_SHORT_LABELS: Record<ModuleKey, string> = {
  recruitment:        "Recruit",
  finance:            "Finance",
  payroll:            "Payroll",
  esign:              "eSign",
  ai_expense_capture: "AI Exp",
  performance:        "Perf",
};

/**
 * Identity colour per module, used for wayfinding once the UI is module-scoped.
 * Recruitment's violet is the one already used for the Candidates nav badge, so
 * the association is pre-existing rather than newly invented.
 */
export const MODULE_COLORS: Record<ModuleKey, string> = {
  recruitment:        "#7c3aed",
  finance:            "#0f766e",
  payroll:            "#b45309",
  esign:              "#4d7c0f",
  ai_expense_capture: "#0369a1",
  performance:        "#be123c",
};

export type ModuleMap = Partial<Record<ModuleKey, boolean>>;

/** True when the tenant owns the module. A missing key means "never purchased". */
export function ownsModule(modules: ModuleMap | undefined, key: ModuleKey): boolean {
  return modules?.[key] === true;
}

/**
 * Which module owns each route prefix.
 *
 * Route URLs stay flat — `/jobs`, never `/recruitment/jobs` — so the active
 * module is derived from the path instead of encoded in it. That keeps every
 * existing bookmark and deep link working, and avoids a redirect layer
 * (spec module-ui-rollout AC6).
 *
 * Mirrors the backend gating in main.py. Anything absent is Core, which is the
 * safe default: an unmapped route stays visible rather than vanishing from the
 * sidebar because someone forgot to list it.
 */
export const MODULE_ROUTE_PREFIXES: Record<ModuleKey, string[]> = {
  recruitment: [
    // "/recruitment-reports" is listed separately on purpose. Matching is exact-or-
    // slash-delimited ("/recruitment" or "/recruitment/..."), so a HYPHENATED sibling
    // is not covered by its stem — "/recruitment-reports" fell through to null, was
    // read as Core, and rendered a RECRUITMENT section inside the People sidebar.
    // Payroll avoids this only because its reports live at "/payroll/reports".
    "/recruitment", "/recruitment-reports",
    "/jobs", "/candidates", "/talent-pool", "/offers", "/interview-hub",
    "/screening", "/recruiter-preferences",
  ],
  finance: [
    "/finance",
    "/invoices", "/quotations", "/billing-notes", "/tax-invoices", "/receipts",
    "/debit-credit-notes", "/sales-credit-debit-notes", "/vendors",
    "/vendor-bills", "/products",
  ],
  // /payroll is the pay-run surface. /my-slips is an employee reading their own
  // payslip and stays Core — an employee must not lose sight of their own pay
  // because the workspace has not bought the Payroll module.
  payroll: ["/payroll", "/payroll-home"],
  performance: ["/performance", "/performance-home"],
  esign: ["/esign", "/esign-home"],
  ai_expense_capture: ["/ai-expense-capture", "/expenses-home"],
};

/**
 * Modules a role can actually use: those with at least one route in the role's
 * shell config (spec: module-launcher-role-scope). Entitlements are tenant-wide
 * with no role dimension, so without this an employee gets offered Payroll in
 * the launcher and lands on an operator dashboard whose every number 403s.
 *
 * Route EXISTENCE, not runtime guard state: feature toggles flipping must not
 * make launcher tiles appear and vanish between renders.
 *
 * The module home paths themselves (in App's moduleDashboardRoutes, not the
 * shell config) don't count as evidence — every role gets those injected, which
 * is exactly the hole this closes.
 */
export function modulesForRoutes(paths: string[]): Set<ModuleKey> {
  const eligible = new Set<ModuleKey>();
  for (const path of paths) {
    const owner = moduleForPath(path);
    if (owner) eligible.add(owner);
  }
  return eligible;
}

/** The module a path belongs to, or null for Core. Longest prefix wins. */
export function moduleForPath(pathname: string): ModuleKey | null {
  let best: ModuleKey | null = null;
  let bestLen = 0;
  for (const key of MODULE_KEYS) {
    for (const prefix of MODULE_ROUTE_PREFIXES[key]) {
      const hit = pathname === prefix || pathname.startsWith(prefix + "/");
      if (hit && prefix.length > bestLen) {
        best = key;
        bestLen = prefix.length;
      }
    }
  }
  return best;
}

/**
 * Core routes that must NOT change which module you are in.
 *
 * These are the System-section links (Settings / Feature Access / Workspace /
 * Security). They belong to no module, so `moduleForPath` returns null for them
 * — but they are also the one group of Core links deliberately pinned into
 * *every* module sidebar, because admin things are not a module's concern.
 *
 * Those two facts together were a bug: someone in Finance clicked Settings, the
 * active module went null, and the whole shell — sidebar, badge, launcher —
 * snapped back to People. A link the module sidebar itself offers must never
 * eject you from that module.
 *
 * So these paths are module-NEUTRAL rather than Core: they keep whatever
 * context you arrived with, and read as Core only when Core is where you were.
 * Module-owned settings (`/payroll/settings`) are untouched — `moduleForPath`
 * matches them first and neutrality never applies.
 *
 * Kept in sync with shellConfig's System sections by
 * tests/test_module_role_access.py::test_system_nav_links_are_module_neutral.
 */
export const MODULE_NEUTRAL_PREFIXES = ["/settings", "/workspace", "/security"];

/** True for a path that belongs to no module and should not switch modules. */
export function isModuleNeutralPath(pathname: string): boolean {
  if (moduleForPath(pathname) !== null) return false;
  return MODULE_NEUTRAL_PREFIXES.some(
    (prefix) => pathname === prefix || pathname.startsWith(prefix + "/"),
  );
}

/**
 * Where each module opens. Used by the launcher and by the landing preference,
 * so they cannot disagree about what "go to Recruitment" means.
 */
export const MODULE_HOME_PATHS: Record<ModuleKey, string> = {
  // Every module opens on its own dashboard. An earlier version sent the
  // single-page modules straight to their page on the grounds that an overview
  // above one page buys nothing — but a product where three modules have a
  // dashboard and three do not reads as unfinished. Consistency wins.
  recruitment: "/recruitment",
  finance: "/finance",
  payroll: "/payroll-home",
  performance: "/performance-home",
  ai_expense_capture: "/expenses-home",
  esign: "/esign-home",
};
